import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";

const DEFAULT_TIMEOUT_SECS = 20;

export default function StealingSignalsModal({ phase, cardCount, revealedCard, allCards, onPick, onDecide, timeoutSecs }) {
  // phase: "pick" | "decide"
  // cardCount: how many blank backs to show
  // revealedCard: card id (when phase === "decide")
  const effectiveTimeout = timeoutSecs > 0 ? timeoutSecs : DEFAULT_TIMEOUT_SECS;
  const [picked, setPicked] = useState(false);
  const [timeLeft, setTimeLeft] = useState(effectiveTimeout);
  const autoFiredRef = useRef(false);

  const card = allCards?.find(c => c.id === revealedCard);

  // Reset timer on phase change so each phase gets its own window.
  useEffect(() => {
    setTimeLeft(effectiveTimeout);
    autoFiredRef.current = false;
    const id = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          if (!autoFiredRef.current) {
            autoFiredRef.current = true;
            if (phase === "pick") {
              setPicked(true);
              onPick();
            } else if (phase === "decide") {
              onDecide("discard");
            }
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <motion.div
        initial={{ scale: 0.92, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-slate-900 border border-amber-500/40 rounded-2xl p-5 w-full max-w-sm mx-4 shadow-2xl"
      >
        <div className="mb-4">
          <div className="flex items-center justify-between mb-1">
            <p className="text-[10px] font-black tracking-widest text-amber-400 uppercase">Stealing Signals</p>
            <span className={`text-xs font-black tabular-nums ${timeLeft <= 5 ? "text-red-400 animate-pulse" : "text-slate-400"}`}>
              {timeLeft}s
            </span>
          </div>
          {phase === "pick" ? (
            <p className="text-sm text-slate-400">Pick one of your opponent's cards (blind).</p>
          ) : (
            <p className="text-sm text-slate-400">You stole a card — what will you do with it?</p>
          )}
        </div>

        <AnimatePresence mode="wait">
          {phase === "pick" && (
            <motion.div key="pick" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <div className="flex gap-2 flex-wrap justify-center mb-5">
                {Array.from({ length: cardCount }, (_, i) => (
                  <button
                    key={i}
                    disabled={picked}
                    onClick={() => { if (!picked) { setPicked(true); onPick(); } }}
                    className="w-16 h-24 rounded-lg border-2 border-amber-500/40 hover:border-amber-400/80 transition-all overflow-hidden disabled:opacity-60 shadow-lg p-0"
                  >
                    <img src="/card-back.png" alt="Card back" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
              {picked && (
                <p className="text-xs text-amber-400 font-bold text-center">Picking…</p>
              )}
            </motion.div>
          )}

          {phase === "decide" && (
            <motion.div key="decide" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <div className="bg-slate-800 border border-amber-500/30 rounded-xl p-4 mb-5">
                <p className="text-xs text-amber-400 font-black uppercase tracking-wider mb-1">Stolen Card</p>
                <p className="text-base font-black text-white">{card?.name || revealedCard}</p>
                {card?.description && (
                  <p className="text-xs text-slate-400 mt-1">{card.description}</p>
                )}
              </div>
              <div className="grid grid-cols-3 gap-2">
                <button
                  onClick={() => onDecide("use")}
                  className="py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-black text-xs transition"
                >
                  Use It
                </button>
                <button
                  onClick={() => onDecide("discard")}
                  className="py-2.5 rounded-lg bg-slate-700 hover:bg-slate-600 text-slate-200 font-black text-xs transition"
                >
                  Discard
                </button>
                <button
                  onClick={() => onDecide("return")}
                  className="py-2.5 rounded-lg bg-slate-700 hover:bg-slate-600 text-slate-200 font-black text-xs transition"
                >
                  Return
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
}
